import { useCallback, useEffect, useRef, useState } from 'react';
import { useCreateOnlyOfficeSessionMutation } from 'store/services/flow/documentEditing';
import type { CreateOnlyOfficeSessionArgs, DocumentSessionInfo, OnlyOfficeSession } from 'types/documentEditing';
import { getDocumentErrorKey, validateDocumentSession } from 'utils/documentEditing/documentEditing';

export const useDocumentSession = (args: CreateOnlyOfficeSessionArgs, origin: string) => {
  const [createSession] = useCreateOnlyOfficeSessionMutation();
  const [attempt, setAttempt] = useState(0);
  const [session, setSession] = useState<DocumentSessionInfo | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const launchSession = useRef<OnlyOfficeSession | null>(null);
  const request = useRef<{ promise: Promise<OnlyOfficeSession> | null } | null>(null);
  const { agendaId, recordId, fileId, mode } = args;

  useEffect(() => {
    let active = true;
    if (!request.current) {
      const operation = createSession({ agendaId, recordId, fileId, mode, suppressErrorToast: true });
      request.current = { promise: operation.unwrap().finally(() => operation.reset()) };
    }
    request.current.promise?.then(
      (response) => {
        if (!active) return;
        try {
          const validated = validateDocumentSession(response, fileId, origin);
          if (validated.mode !== mode && mode !== 'auto') throw new Error('Unexpected document mode');
          launchSession.current = validated;
          const { accessToken: _accessToken, ...metadata } = validated;
          setSession(metadata);
        } catch {
          setErrorKey('user.documentEditor.invalidSession');
        }
        if (request.current) request.current.promise = null;
      },
      (error: unknown) => {
        if (!active) return;
        setErrorKey(getDocumentErrorKey(error));
        if (request.current) request.current.promise = null;
      }
    );
    return () => {
      active = false;
    };
  }, [agendaId, recordId, fileId, mode, origin, attempt, createSession]);

  const takeLaunchSession = useCallback(() => {
    const value = launchSession.current;
    launchSession.current = null;
    return value;
  }, []);

  const reopen = useCallback(() => {
    launchSession.current = null;
    request.current = null;
    setSession(null);
    setErrorKey(null);
    setAttempt((value) => value + 1);
  }, []);

  return { session, errorKey, takeLaunchSession, reopen };
};
